import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { datadogRum } from '@datadog/browser-rum';
import { yupResolver } from '@hookform/resolvers/yup';
import { AxiosResponse } from 'axios';
import { useRouter } from 'next/router';

import { getAxiosError } from 'utils/getAxiosError';
import { axiosFactory } from 'utils/http/axiosFactory';
import { sendToErrorTracking } from 'utils/sendToErrorTracking';
import { ValidationContactSchema } from 'utils/validations/ValidationContactSchema';

type InputsProps = {
  company_name: string;
  name: string;
  surname: string;
  phone: string;
  email: string;
};

export const useSubmit = () => {
  const { i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const router = useRouter();
  const fetcher = axiosFactory(i18n.language, 'get-in-touch');
  const {
    handleSubmit,
    control,
    clearErrors,
    formState: { errors },
  } = useForm<InputsProps>({
    resolver: yupResolver(ValidationContactSchema),
  });

  const onSubmit = async (data: InputsProps) => {
    if (loading) return;

    clearErrors();

    setLoading(true);

    try {
      const response: AxiosResponse = await fetcher.post('/', data);

      if (response.status === 200) setSent(true);

      datadogRum.addAction('GetInTouchMessage', {
        data,
      });
    } catch (error) {
      sendToErrorTracking({ error, where: 'getInTouch', context: data });

      const errStatus = getAxiosError(error)?.response?.status;

      if (errStatus && errStatus >= 400) router.push('/500');
    } finally {
      setLoading(false);
    }
  };

  return {
    handleSubmit,
    onSubmit,
    control,
    errors,
    Controller,
    loading,
    sent,
  };
};
